import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";
import AOS from "aos";
import "aos/dist/aos.css";

type RefreshOn = "path" | "search" | "hash" | "all";

type AOSWrapperProps = {
  children: React.ReactNode;
  /** AOS.init options */
  options?: AOS.AosOptions;
  /** What changes should trigger a refresh */
  refreshOn?: RefreshOn;
  /** Watch DOM mutations and refresh when nodes are added */
  observeMutations?: boolean;
  /** Use refreshHard() on route changes (more aggressive) */
  hardRefreshOnRouteChange?: boolean;
  /** Debounce (ms) for mutation-triggered refresh */
  mutationDebounceMs?: number;
};

type AOSContextValue = {
  refresh: (hard?: boolean) => void;
};

const AOSContext = createContext<AOSContextValue | null>(null);
export const useAOS = () => {
  const ctx = useContext(AOSContext);
  if (!ctx) {
    throw new Error("useAOS must be used within <AOSWrapper>");
  }
  return ctx;
};

// Simple debounce
function debounce<T extends (...args: any[]) => void>(fn: T, wait = 150) {
  let t: number | undefined;
  return (...args: Parameters<T>) => {
    if (t) window.clearTimeout(t);
    t = window.setTimeout(() => fn(...args), wait);
  };
}

export function AOSWrapper({
  children,
  options,
  refreshOn = "path",
  observeMutations = true,
  hardRefreshOnRouteChange = false,
  mutationDebounceMs = 150,
}: AOSWrapperProps) {
  const location = useLocation();
  const initializedRef = useRef(false);

  // Stable refresh methods
  const refresh = (hard?: boolean) => {
    if (typeof window === "undefined") return;
    // Rerun on next paint so DOM is settled
    requestAnimationFrame(() => {
      try {
        (hard ? AOS.refreshHard : AOS.refresh)();
      } catch {
        // no-op
      }
    });
  };

  const ctxValue = useMemo<AOSContextValue>(() => ({ refresh }), []);

  // Init once (SSR safe), with Reduced Motion support via disable option
  useEffect(() => {
    if (typeof window === "undefined" || initializedRef.current) return;

    const reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");

    AOS.init({
      duration: 800,
      once: true,
      // AOS supports boolean/function for disable
      disable: () => reducedMotionQuery?.matches ?? false,
      ...(options ?? {}),
    });

    initializedRef.current = true;

    // Re-init if reduced-motion flips while app is open
    const listener = () => {
      // When preference changes, re-init so AOS applies/clears disable
      AOS.refreshHard();
    };
    reducedMotionQuery?.addEventListener?.("change", listener);
    return () => reducedMotionQuery?.removeEventListener?.("change", listener);
  }, [options]);

  // Refresh on route changes
  useEffect(() => {
    // Choose what part of location triggers refresh
    const key =
      refreshOn === "all"
        ? `${location.pathname}|${location.search}|${location.hash}`
        : refreshOn === "path"
        ? location.pathname
        : refreshOn === "search"
        ? location.search
        : location.hash;

    // Use hard or soft refresh after navigation
    refresh(hardRefreshOnRouteChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.hash, refreshOn, hardRefreshOnRouteChange]);

  // Refresh after images load (prevents missed triggers when images change layout)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const imgs = Array.from(document.querySelectorAll<HTMLImageElement>("img"));
    if (imgs.length === 0) return;

    const onLoad = debounce(() => refresh(false), 100);
    imgs.forEach((img) => img.addEventListener("load", onLoad));
    return () => {
      imgs.forEach((img) => img.removeEventListener("load", onLoad));
    };
  }, [location.pathname, location.search, location.hash]);

  // Observe DOM mutations (content injected after render)
  useEffect(() => {
    if (!observeMutations || typeof window === "undefined") return;

    const debounced = debounce(() => refresh(false), mutationDebounceMs);
    const observer = new MutationObserver((mutations) => {
      // Only react when nodes are added (new animatable elements)
      const anyAdded = mutations.some((m) => (m.addedNodes?.length ?? 0) > 0);
      if (anyAdded) debounced();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [observeMutations, mutationDebounceMs]);

  return <AOSContext.Provider value={ctxValue}>{children}</AOSContext.Provider>;
}
